import { createSelector } from 'reselect';
import _omit from 'lodash/omit';
import _get from 'lodash/get';

import { getFeatures } from 'store/services/features/selectors';
import { getFeaturesMap } from 'store/utils/intents';

import { INTENTS_FILTER_OPTIONS } from 'constants/intentsOptions';

export const getAllIntents = ({ intents }) => intents.all.data;

export const getConstraintSetsWithoutConditions = (constraintSets = []) =>
  constraintSets.filter(cs => cs.conditions && !cs.conditions.length).length;

export const getIsLoadingAll = ({ intents }) => intents.all.isLoading;

export const getIntentsList = ({ intents }) => intents.list.data.items;

export const getIntentsListCount = ({ intents }) => intents.list.data.count;

export const getIntent = ({ intents }) => intents.show.data;

export const getIntentIsLoading = ({ intents }) => intents.show.isLoading;

export const getIsLoadingList = ({ intents }) => intents.list.isLoading;

export const getPaginationOptions = ({ intents }) =>
  intents.list.paginationOptions;

export const getIsAdding = ({ intents }) => intents.add.isLoading;

export const getIsUpdating = ({ intents }) => intents.update.isLoading;

export const getIsDeleting = ({ intents }) => intents.delete.isLoading;

export const getSortOptions = ({ intents }) => intents.list.sortOptions;

export const getFilterOptions = ({ intents }) => intents.list.filterOptions;

export const getFilterParams = ({ intents }) => intents.list.filterParams;

export const getIsExporting = ({ intents }) => intents.partialExport.isLoading;

export const getIsImporting = ({ intents }) => intents.partialImport.isLoading;

export const getImportError = ({ intents }) =>
  _get(intents, 'partialImport.error.response.data', {});

export const getFilterOptionsWithValues = createSelector(
  getFilterOptions,
  values => ({
    values,
    options: INTENTS_FILTER_OPTIONS.map(item => _omit(item, ['value'])),
  }),
);

export const getFilteredSortOptions = createSelector(
  getSortOptions,
  getPaginationOptions,
  (sortOptions, paginationOptions) =>
    sortOptions.map(option => ({
      ...option,
      selected: option.value === paginationOptions.ordering,
    })),
);

export const getSelectedSortOption = createSelector(
  getFilteredSortOptions,
  options => options.find(({ selected }) => selected === true),
);

export const getArchivedFilterOptions = createSelector(
  getFilterOptions,
  filterOptions =>
    _omit(filterOptions.find(({ filterBy }) => filterBy === 'archived'), [
      'type',
      'filterBy',
      'label',
      'enabled',
    ]),
);

export const getFilterOptionsWithSelected = createSelector(
  getFilterOptions,
  getPaginationOptions,
  (filterOptions, paginationOptions) =>
    filterOptions.map(option => ({
      ...option,
      selected: option.archived === paginationOptions.archived,
    })),
);

export const getSelectedFilterOption = createSelector(
  getFilterOptionsWithSelected,
  filterOptions => filterOptions.find(({ selected }) => selected === true),
);

export const getIntentIndex = createSelector(
  getIntentsList,
  getIntent,
  (list, item) => {
    const index = list.findIndex(({ id }) => id === item.id);

    return index === -1 ? 0 : index;
  },
);

export const getIsTraining = ({ intents }) => intents.train.isLoading;
export const getIsTrainingError = ({ intents }) => intents.train.error;

export const getAllIntentsExceptTheCurrent = createSelector(
  getAllIntents,
  getIntent,
  (list, item) => list.filter(_item => _item.id !== item.id),
);

export const getIntentWithPublicFeaturesMap = createSelector(
  getIntent,
  getFeatures,
  (intent, featureTypes) => ({
    ...intent,
    xpackConstraintSetsAreActive: !intent.override,
    constraint_sets: (intent.constraint_sets || []).map(constraintSet => ({
      ...constraintSet,
      conditions: constraintSet.conditions.map(condition => ({
        ...condition,
        entity_reference_obj: {
          ...condition.entity_reference_obj,
          featuresMap: getFeaturesMap(
            condition.entity_reference_obj.features,
            featureTypes,
          ),
        },
      })),
    })),
  }),
);
